import { ReactNode, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "src/apps/hooks/useAuth";
import { useDependencies } from "src/apps/hooks/useDependencies";
import { useGetUserLink } from "src/apps/hooks/useGetUserLink";
import type {
  AnalyticsEvents,
  AnalyticsProperties,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { AnalyticsContext } from "./AnalyticsContext";
import { useGetCustomerCustomization } from "src/apps/hooks/useGetCustomization";
import { DocumentationAreaId } from "src/contexts/documentationArea/domain/DocumentationAreaId";
import { useDocumentationArea } from "../hooks/useDocumentationArea";
import { ActorId } from "src/contexts/documentationArea/domain/ActorId";

type QueuedEvent = {
  type: AnalyticsEvents;
  properties?: AnalyticsProperties;
  userProps?: AnalyticsProperties;
};

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const { documentationAreaController } = useDependencies();
  const { userLink } = useGetUserLink();
  const { documentationArea } = useDocumentationArea();
  const { loginType } = useGetCustomerCustomization();

  const pendingEventsRef = useRef<QueuedEvent[]>([]);
  const isFlushingRef = useRef(false);
  const [canSend, setCanSend] = useState(false);

  const sendDocumentationAreaEvent = useCallback(
    async (event: QueuedEvent, documentationAreaId: DocumentationAreaId, actorId: ActorId) => {
      try {
        await documentationAreaController.track(
          token,
          documentationAreaId,
          actorId,
          event.type,
          { ...event.properties, loginType },
          event.userProps,
        );
      } catch (error) {
        console.error("Failed to send analytics event", { event, error });
      }
    },
    [documentationArea, documentationAreaController, token, loginType],
  );

  const sendUserLinkEvent = useCallback(
    async (event: QueuedEvent, userLinkId: string) => {
      try {
        await documentationAreaController.trackUserLink(
          userLinkId,
          event.type,
          { ...event.properties, loginType },
          event.userProps,
        );
      } catch (error) {
        console.error("Failed to send analytics event", { event, error });
      }
    },
    [documentationAreaController, userLink, loginType],
  );

  const sendNow = useCallback(
    async (event: QueuedEvent) => {
      try {
        if (canSend) {
          const documentationAreaId = documentationArea?.getId();
          const actorId = documentationArea?.getActor().getId();
          const userLinkId = userLink?.id;

          if (documentationAreaId && actorId)
            await sendDocumentationAreaEvent(event, documentationAreaId, actorId);
          else if (userLinkId) await sendUserLinkEvent(event, userLinkId);
        }
      } catch (error) {
        console.error("Failed to send analytics event", { event, error });
      }
    },
    [
      documentationArea,
      documentationAreaController,
      token,
      userLink,
      canSend,
      sendDocumentationAreaEvent,
      sendUserLinkEvent,
    ],
  );

  useEffect(() => {
    setCanSend((prev) => prev || Boolean(userLink?.id));
  }, [userLink]);

  useEffect(() => {
    setCanSend((prev) => prev || Boolean(documentationArea && token));
  }, [documentationArea, token]);

  const flushQueue = useCallback(async () => {
    if (isFlushingRef.current) return;
    if (pendingEventsRef.current.length === 0) return;

    isFlushingRef.current = true;
    // Atomically take ownership of the current queue to avoid duplicates if the
    // identifiers change while we're awaiting.
    const events = pendingEventsRef.current;
    pendingEventsRef.current = [];

    try {
      for (const event of events) {
        await sendNow(event);
      }
    } finally {
      isFlushingRef.current = false;
    }
  }, [sendNow]);

  useEffect(() => {
    if (!canSend) return;
    void flushQueue();
  }, [canSend, flushQueue]);

  const sendEvent = useCallback(
    (type: AnalyticsEvents, properties?: AnalyticsProperties, userProps?: AnalyticsProperties) => {
      const event: QueuedEvent = { type, properties, userProps };
      if (canSend) {
        void sendNow(event);
        return;
      }

      pendingEventsRef.current.push(event);
    },
    [canSend, sendNow],
  );

  return <AnalyticsContext.Provider value={{ sendEvent }}>{children}</AnalyticsContext.Provider>;
}
